// Website copy — customer-facing brand story
// Structure: SITE[section][lang]

export const SITE = {
    hero: {
        en: {
            kicker: "MMXXVII · Chapter II",
            title: ["Born from", "an intolerance", "for the average."],
            payoff: "Italian Maison — Rare Editions",
            intro:
                "We began in a small laboratory. A gluten-free kitchen for those who had been told to choose between health and pleasure. We refused the choice. We are still refusing it — only now, in a language wider than a kitchen.",
            cta1: "Read our origin",
            cta2: "Follow the announcement",
        },
        it: {
            kicker: "MMXXVII · Capitolo II",
            title: ["Nati da", "un'intolleranza", "per la media."],
            payoff: "Maison Italiana — Edizioni Rare",
            intro:
                "Siamo nati in un piccolo laboratorio. Una cucina senza glutine per chi era stato costretto a scegliere tra salute e piacere. Ci siamo rifiutati di scegliere. Continuiamo a rifiutarci — solo, adesso, in una lingua più ampia di una cucina.",
            cta1: "Leggi le origini",
            cta2: "Segui l'annuncio",
        },
    },
    origins: {
        en: {
            kicker: "I — Origins",
            title: "A Kitchen Without Compromise",
            paragraphs: [
                "Intollerantia was born as a gluten-free gastronomic laboratory — a place where people who could not eat what everyone else ate would find, on the contrary, more than what everyone else ate.",
                "We were known, in those years, for a discipline: we did not accept every ingredient, we did not accept every partner, we did not accept every shortcut. Each collaboration was selected — as a curator selects a room, not as a merchant fills a shelf.",
                "We learned that intolerance is not a condition. It is an ethical filter. And the same filter that saved a coeliac guest could save a whole category of Italian excellence: the one nobody was defending.",
            ],
        },
        it: {
            kicker: "I — Origini",
            title: "Una Cucina Senza Compromesso",
            paragraphs: [
                "Intollerantia è nata come laboratorio gastronomico senza glutine — un luogo in cui chi non poteva mangiare ciò che mangiavano tutti trovava, al contrario, più di ciò che mangiavano tutti.",
                "Eravamo conosciuti, in quegli anni, per una disciplina: non accettavamo ogni ingrediente, non accettavamo ogni partner, non accettavamo ogni scorciatoia. Ogni collaborazione veniva selezionata — come un curatore seleziona una sala, non come un mercante riempie uno scaffale.",
                "Abbiamo imparato che l'intolleranza non è una condizione. È un filtro etico. E lo stesso filtro che salvava un ospite celiaco poteva salvare un'intera categoria di eccellenza italiana: quella che nessuno stava difendendo.",
            ],
        },
    },
    evolution: {
        en: {
            kicker: "II — Evolution",
            title: "From Laboratory To Maison",
            body: "The next chapter is wider than a kitchen. Intollerantia becomes a maison — a house that publishes editions of what our land already knows how to do best, but has stopped naming with care.",
            steps: [
                {
                    year: "2019",
                    label: "The Laboratory",
                    body: "Gluten-free kitchen. Selected collaborations. A discipline against the shortcut.",
                },
                {
                    year: "2020",
                    label: "The Filter",
                    body: "We understood that our real product was a criterion — not a recipe.",
                },
                {
                    year: "2026",
                    label: "The Change",
                    body: "The kitchen closes as a laboratory and reopens as a maison. The passage is announced.",
                },
                {
                    year: "2027",
                    label: "The Release",
                    body: "First commercial edition. Numbered. Published. Placed in the hands that were waiting.",
                },
            ],
        },
        it: {
            kicker: "II — Evoluzione",
            title: "Dal Laboratorio Alla Maison",
            body: "Il prossimo capitolo è più ampio di una cucina. Intollerantia diventa una maison — una casa che pubblica edizioni di ciò che la nostra terra sa già fare meglio, ma ha smesso di nominare con cura.",
            steps: [
                {
                    year: "2019",
                    label: "Il Laboratorio",
                    body: "Cucina senza glutine. Collaborazioni selezionate. Una disciplina contro la scorciatoia.",
                },
                {
                    year: "2020",
                    label: "Il Filtro",
                    body: "Abbiamo capito che il nostro vero prodotto era un criterio — non una ricetta.",
                },
                {
                    year: "2026",
                    label: "Il Cambiamento",
                    body: "La cucina chiude come laboratorio e riapre come maison. Il passaggio viene annunciato.",
                },
                {
                    year: "2027",
                    label: "L'Uscita in Commercio",
                    body: "Prima edizione commerciale. Numerata. Pubblicata. Consegnata alle mani che aspettavano.",
                },
            ],
        },
    },
    mission: {
        en: {
            kicker: "III — Mission",
            title: "What We Will Do",
            lead:
                "To make known the selected products of our land — those that Italy has produced quietly, for centuries, without a maison willing to publish them properly.",
            pillars: [
                {
                    n: "01",
                    label: "Select",
                    body: "One estate. One name. One responsibility. We do not build a catalogue. We compose an edition.",
                },
                {
                    n: "02",
                    label: "Publish",
                    body: "Every product is issued as an editor issues a rare book — numbered, signed, in a run that ends.",
                },
                {
                    n: "03",
                    label: "Reveal",
                    body: "We name the field, the farmer, the November light, the exact vessel. Nothing is generic. Nothing is anonymous.",
                },
                {
                    n: "04",
                    label: "Refuse",
                    body: "We refuse volume, we refuse gloss, we refuse the language of luxury. We prefer the language of place.",
                },
            ],
        },
        it: {
            kicker: "III — Missione",
            title: "Cosa Faremo",
            lead:
                "Far conoscere i prodotti selezionati della nostra terra — quelli che l'Italia ha prodotto in silenzio, per secoli, senza una maison disposta a pubblicarli come si deve.",
            pillars: [
                {
                    n: "01",
                    label: "Selezionare",
                    body: "Una tenuta. Un nome. Una responsabilità. Non costruiamo un catalogo. Componiamo un'edizione.",
                },
                {
                    n: "02",
                    label: "Pubblicare",
                    body: "Ogni prodotto è pubblicato come un editore pubblica un libro raro — numerato, firmato, in una tiratura che finisce.",
                },
                {
                    n: "03",
                    label: "Rivelare",
                    body: "Nominiamo il campo, il contadino, la luce di novembre, il recipiente esatto. Nulla è generico. Nulla è anonimo.",
                },
                {
                    n: "04",
                    label: "Rifiutare",
                    body: "Rifiutiamo il volume, il glossato, la lingua del lusso. Preferiamo la lingua del luogo.",
                },
            ],
        },
    },
    what: {
        en: {
            kicker: "IV — What We Are",
            title: "A House Of Rare Editions",
            body: "We are not a brand of many products. We are a maison of few, chosen, well-published editions of the Italian territory. Our first edition is olive oil from Western Sicily. The next editions — food, beauty, home — will follow when they are ready to be named.",
            categories: [
                {
                    id: "food",
                    label: "Food",
                    line: "Extra virgin olive oil, salt, honey. Western Sicily first.",
                    open: true,
                },
                {
                    id: "beauty",
                    label: "Beauty",
                    line: "The same olive, decanted for the skin. Under composition.",
                    open: false,
                },
                {
                    id: "home",
                    label: "Home",
                    line: "Objects that do not consume — they last. In study.",
                    open: false,
                },
            ],
            reserved: "In preparation",
            available: "First edition",
        },
        it: {
            kicker: "IV — Chi Siamo Ora",
            title: "Una Casa Di Edizioni Rare",
            body: "Non siamo un marchio di molti prodotti. Siamo una maison di poche, scelte, ben pubblicate edizioni del territorio italiano. La nostra prima edizione è l'olio extravergine della Sicilia occidentale. Le prossime — food, beauty, casa — arriveranno quando saranno pronte a essere nominate.",
            categories: [
                {
                    id: "food",
                    label: "Food",
                    line: "Olio extravergine, sale, miele. Prima la Sicilia occidentale.",
                    open: true,
                },
                {
                    id: "beauty",
                    label: "Beauty",
                    line: "Lo stesso oliveto, decantato per la pelle. In composizione.",
                    open: false,
                },
                {
                    id: "home",
                    label: "Casa",
                    line: "Oggetti che non consumano — durano. In studio.",
                    open: false,
                },
            ],
            reserved: "In preparazione",
            available: "Prima edizione",
        },
    },
    place: {
        en: {
            kicker: "V — Place",
            title: "Where We Look From",
            body: "Trapani. Marettimo. The salt pans, the November wind, the sea that has never been generic. From this angle we look at Italy — and we choose.",
            coord: "37° 58′ N · 12° 03′ E",
        },
        it: {
            kicker: "V — Luogo",
            title: "Da Dove Guardiamo",
            body: "Trapani. Marettimo. Le saline, il vento di novembre, il mare che non è mai stato generico. Da questa angolazione guardiamo l'Italia — e scegliamo.",
            coord: "37° 58′ N · 12° 03′ E",
        },
    },
    salt: {
        en: {
            kicker: "V.I — The Territory Of Salt",
            title: "The Salt Pans Of Trapani",
            lead:
                "Between Trapani and Marsala, water becomes rose, then white. It is the oldest editorial act of this coast — the sea publishes itself, one crystal at a time, under a light that only appears here.",
            body: [
                "The salt of Trapani is harvested by hand from July to September, gathered into pyramids that reflect on the pink surface of the vasca.",
                "The windmill towers — Torre di Nubia among them — do not decorate the horizon: they operate it. They lift the brine, they move the water, they still work.",
                "We look here first when we choose an ingredient. If the salt of this place has been published for two thousand years without a name too loud, then everything we publish must be able to stand next to it.",
            ],
            caption: "Riserva Naturale Saline di Trapani e Paceco · WWF",
            metaLeft: "Harvest · July → September",
            metaRight: "Method · Wind, sun, hand",
            objectsKicker: "V.II — The Objects",
            objectsTitle: "Two Editions Of The Same Sea",
            objectsBody:
                "Two glass vessels. One salt. Two grains — the coarse for the plate that meets fire, the flake for the plate that meets air.",
            products: [
                {
                    id: "grani",
                    label: "Sale di Trapani",
                    sub: "Sea Salt · Coarse",
                    details: "Iodinated · Hand-harvested",
                    body:
                        "Coarse crystal. Iodine of the Mediterranean. Harvested from the northern vasche of the Trapani reserve, dried by wind alone, decanted into ivory-labelled glass.",
                    format: "Ø 65mm · h 130mm · 250g",
                },
                {
                    id: "fiocchi",
                    label: "Fiocchi di Sale",
                    sub: "Salt Flakes",
                    details: "Iodinated · Hand-harvested",
                    body:
                        "The final crust of the vasca — the layer that forms only when the sun and the wind agree. Broken by hand into thin flakes. For the last gesture before the plate leaves the kitchen.",
                    format: "Ø 65mm · h 130mm · 120g",
                },
            ],
        },
        it: {
            kicker: "V.I — Il Territorio del Sale",
            title: "Le Saline Di Trapani",
            lead:
                "Tra Trapani e Marsala, l'acqua diventa rosa, poi bianca. È l'atto editoriale più antico di questa costa — il mare pubblica se stesso, un cristallo alla volta, sotto una luce che appare solo qui.",
            body: [
                "Il sale di Trapani viene raccolto a mano da luglio a settembre, radunato in piramidi che si specchiano sulla superficie rosa della vasca.",
                "I mulini a vento — la Torre di Nubia tra loro — non decorano l'orizzonte: lo mettono in moto. Sollevano la salamoia, muovono l'acqua, lavorano ancora.",
                "Guardiamo qui, per primi, quando scegliamo un ingrediente. Se il sale di questo luogo è stato pubblicato per duemila anni senza un nome troppo forte, allora tutto ciò che pubblichiamo deve poter stare accanto a lui.",
            ],
            caption: "Riserva Naturale Saline di Trapani e Paceco · WWF",
            metaLeft: "Raccolta · Luglio → Settembre",
            metaRight: "Metodo · Vento, sole, mano",
            objectsKicker: "V.II — Gli Oggetti",
            objectsTitle: "Due Edizioni Dello Stesso Mare",
            objectsBody:
                "Due recipienti di vetro. Un solo sale. Due grane — il grosso per il piatto che incontra il fuoco, il fiocco per il piatto che incontra l'aria.",
            products: [
                {
                    id: "grani",
                    label: "Sale di Trapani",
                    sub: "Sale Marino · Grosso",
                    details: "Iodato · Raccolto a mano",
                    body:
                        "Cristallo grosso. Iodio del Mediterraneo. Raccolto dalle vasche settentrionali della riserva di Trapani, asciugato dal solo vento, versato in vetro con etichetta avorio.",
                    format: "Ø 65mm · h 130mm · 250g",
                },
                {
                    id: "fiocchi",
                    label: "Fiocchi di Sale",
                    sub: "Fiocchi",
                    details: "Iodato · Raccolto a mano",
                    body:
                        "L'ultima crosta della vasca — lo strato che si forma solo quando il sole e il vento sono d'accordo. Spezzato a mano in fiocchi sottili. Per l'ultimo gesto prima che il piatto lasci la cucina.",
                    format: "Ø 65mm · h 130mm · 120g",
                },
            ],
        },
    },
    founder: {
        en: {
            kicker: "VI — Founder's Note",
            title: "A Personal Word",
            body: [
                "We did not start Intollerantia to sell more.",
                "We started it because a table without care is a small betrayal, repeated three times a day.",
                "We continue it because Italy has more to give than what its shelves are willing to publish.",
            ],
            sign: "The Founder",
        },
        it: {
            kicker: "VI — Nota del Fondatore",
            title: "Una Parola Personale",
            body: [
                "Non abbiamo cominciato Intollerantia per vendere di più.",
                "Abbiamo cominciato perché una tavola senza cura è un piccolo tradimento, ripetuto tre volte al giorno.",
                "Continuiamo perché l'Italia ha più da dare di quello che i suoi scaffali sono disposti a pubblicare.",
            ],
            sign: "Il Fondatore",
        },
    },
    newsletter: {
        en: {
            kicker: "VII — Circle",
            title: "Enter The Reader's Circle",
            body: "We write once, at each edition. No promotions, no discounts. Only the announcement of what has been chosen, and why.",
            emailLabel: "Your email",
            placeholder: "name@maison.com",
            submit: "Join",
            sending: "Sending…",
            ok: "You will be written to.",
            err: "Something went wrong. Please retry.",
        },
        it: {
            kicker: "VII — Circolo",
            title: "Entra nel Circolo dei Lettori",
            body: "Scriviamo una volta, a ogni edizione. Nessuna promozione, nessuno sconto. Solo l'annuncio di ciò che è stato scelto, e perché.",
            emailLabel: "La tua email",
            placeholder: "nome@maison.com",
            submit: "Iscriviti",
            sending: "Invio…",
            ok: "Ti scriveremo.",
            err: "Qualcosa è andato storto. Riprova.",
        },
    },
    footer: {
        en: {
            legal: "© MMXXVII · Intollerantia® — Italian Maison, Rare Editions.",
            addr: "Trapani · Sicilia · Italia",
            contact: "Contact",
            reserved: "All rights reserved.",
        },
        it: {
            legal: "© MMXXVII · Intollerantia® — Maison Italiana, Edizioni Rare.",
            addr: "Trapani · Sicilia · Italia",
            contact: "Contatto",
            reserved: "Tutti i diritti riservati.",
        },
    },
    announcement: {
        en: {
            kicker: "Now Announcing",
            title: "The Birth Of A Maison",
            lead:
                "MMXXVII marks the passage. From a discipline practised in silence for a decade, to a maison ready to publish. This is the announcement of what happens next.",
            status: "First Edition · Autumn MMXXVII",
            stage: "In composition",
            actionLabel: "Press & Media",
            actionEmail: "intollerantia@gmail.com",
            mediaLabel: "Press kit",
            mediaBody:
                "For editorial features, distribution enquiries, and founding-edition allocation.",
        },
        it: {
            kicker: "Annuncio",
            title: "La Nascita Di Una Maison",
            lead:
                "MMXXVII segna il passaggio. Da una disciplina praticata in silenzio per un decennio, a una maison pronta a pubblicare. Questo è l'annuncio di ciò che accade adesso.",
            status: "Prima Edizione · Autunno MMXXVII",
            stage: "In composizione",
            actionLabel: "Press & Media",
            actionEmail: "intollerantia@gmail.com",
            mediaLabel: "Cartella stampa",
            mediaBody:
                "Per approfondimenti editoriali, richieste di distribuzione e allocazione dell'edizione fondativa.",
        },
    },
    press: {
        en: {
            kicker: "Editorial · MMXXVII",
            title: "Where The Story Is Told",
            outlets: [
                "Vogue Italia",
                "Wallpaper*",
                "Kinfolk",
                "Cereal",
                "Monocle",
                "Elle Décor",
                "Domus",
            ],
            note: "Editorial coverage pending — press enquiries welcome.",
        },
        it: {
            kicker: "Editoriale · MMXXVII",
            title: "Dove Viene Raccontata La Storia",
            outlets: [
                "Vogue Italia",
                "Wallpaper*",
                "Kinfolk",
                "Cereal",
                "Monocle",
                "Elle Décor",
                "Domus",
            ],
            note: "Copertura editoriale in via di definizione — richieste stampa benvenute.",
        },
    },
    follow: {
        en: {
            kicker: "VIII — Follow The Announcement",
            title: "Two Silent Channels",
            body:
                "We do not run campaigns. We publish once, at each edition. Follow to receive the first line — before the shelf, before the press.",
            channels: [
                {
                    id: "instagram",
                    label: "Instagram",
                    handle: "@intollerantia",
                    url: "https://www.instagram.com/intollerantia",
                    note: "Editorial grid. Two photographs, one still, one text.",
                },
                {
                    id: "email",
                    label: "Direct Line",
                    handle: "intollerantia@gmail.com",
                    url: "mailto:intollerantia@gmail.com",
                    note: "Press, distribution, private allocation.",
                },
            ],
        },
        it: {
            kicker: "VIII — Segui L'Annuncio",
            title: "Due Canali Silenziosi",
            body:
                "Non facciamo campagne. Pubblichiamo una volta, a ogni edizione. Segui per ricevere la prima riga — prima dello scaffale, prima della stampa.",
            channels: [
                {
                    id: "instagram",
                    label: "Instagram",
                    handle: "@intollerantia",
                    url: "https://www.instagram.com/intollerantia",
                    note: "Griglia editoriale. Due fotografie, una still, un testo.",
                },
                {
                    id: "email",
                    label: "Linea Diretta",
                    handle: "intollerantia@gmail.com",
                    url: "mailto:intollerantia@gmail.com",
                    note: "Stampa, distribuzione, allocazione privata.",
                },
            ],
        },
    },
    honey: {
        en: {
            kicker: "VI — The Honey Of Marettimo",
            title: "Miele di Cardo Selvatico",
            place: "Isola di Marettimo · Egadi",
            lead:
                "Wild thistle grows where no one plants it. On the ridges of Marettimo — a stone in the sea, three hours from Trapani by boat — bees find purple heads of cardo in a wind that never stops. This is the honey we chose.",
            body: [
                "The comb is opened cold. No heating, no filtering with cloth or heat. The honey descends by gravity into the glass — slower than a season, but with everything the flower said still audible.",
                "Amber, dense, with the salt of the island in the mouth. Bitter for a second, sweet for the rest. It behaves on the palate the way Marettimo behaves on the horizon: it holds.",
                "Limited edition. Numbered by hand. Never repeated once the wind changes.",
            ],
            meta: [
                { k: "Origin", v: "Marettimo · Isole Egadi" },
                { k: "Bloom", v: "Wild cardo · June" },
                { k: "Method", v: "Cold-extracted · Unfiltered" },
                { k: "Format", v: "Ø 65mm · h 130mm · 250g" },
            ],
            edition: "Edizione Limitata · MMXXVII",
            objectLabel: "Ed. 03",
            fig: "Fig. IV · The vessel · One kilogram of stillness",
        },
        it: {
            kicker: "VI — Il Miele di Marettimo",
            title: "Miele di Cardo Selvatico",
            place: "Isola di Marettimo · Egadi",
            lead:
                "Il cardo selvatico cresce dove nessuno lo pianta. Sui crinali di Marettimo — una pietra in mare, tre ore da Trapani in barca — le api trovano le teste viola del cardo in un vento che non si ferma mai. Questo è il miele che abbiamo scelto.",
            body: [
                "Il favo viene aperto a freddo. Nessun riscaldamento, nessuna filtrazione con tela o calore. Il miele scende per gravità nel vetro — più lento di una stagione, ma con tutto quello che il fiore ha detto ancora udibile.",
                "Ambrato, denso, con il sale dell'isola in bocca. Amaro per un secondo, dolce per tutto il resto. Si comporta al palato come Marettimo si comporta all'orizzonte: tiene.",
                "Edizione limitata. Numerata a mano. Mai ripetuta una volta cambiato il vento.",
            ],
            meta: [
                { k: "Origine", v: "Marettimo · Isole Egadi" },
                { k: "Fioritura", v: "Cardo selvatico · Giugno" },
                { k: "Metodo", v: "Estrazione a freddo · Non filtrato" },
                { k: "Formato", v: "Ø 65mm · h 130mm · 250g" },
            ],
            edition: "Edizione Limitata · MMXXVII",
            objectLabel: "Ed. 03",
            fig: "Fig. IV · Il recipiente · Un chilo di quiete",
        },
    },
    nav: {
        en: {
            origins: "Origins",
            evolution: "Evolution",
            mission: "Mission",
            editions: "Editions",
            place: "Place",
            circle: "Circle",
            announcement: "Announcement",
            follow: "Follow",
            salt: "Salt",
            honey: "Honey",
        },
        it: {
            origins: "Origini",
            evolution: "Evoluzione",
            mission: "Missione",
            editions: "Edizioni",
            place: "Luogo",
            circle: "Circolo",
            announcement: "Annuncio",
            follow: "Segui",
            salt: "Sale",
            honey: "Miele",
        },
    },
};
